import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Globe } from 'lucide-react'
import { useLocale } from '@/stores/locale'
import { cn } from '@/lib/utils'
import { isEnPublicPath, isPublicPath, toEnPath, toZhPath } from '@/lib/publicLocale'
import type { Locale } from '@/i18n/config'

export function LanguageSwitch({ className, compact }: { className?: string; compact?: boolean }) {
  const { t, i18n } = useTranslation()
  const { locale, setLocale } = useLocale()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  // 高亮以 i18n 当前语言为准（受控），store 仅作为切换入口 — 两者通过本 effect 双向同步，
  // 彻底避免「内容已切语言但胶囊高亮不跟随」的失步。
  const activeLocale: Locale = i18n.language === 'en' ? 'en' : 'zh'
  useEffect(() => {
    if (locale !== activeLocale) setLocale(activeLocale)
  }, [activeLocale, locale, setLocale])

  // 公开页语言与 URL 绑定（/en/* 强制 en — hreflang/OG 语义，见 src/lib/publicLocale.ts）：
  // 切换 = 跳到对应语言的 URL（+ 持久化偏好，控制台接着生效）。控制台维持纯 setLocale。
  const onPublic = isPublicPath(pathname)
  const onEn = isEnPublicPath(pathname)
  const switchTo = (target: Locale) => {
    if (!onPublic) return setLocale(target)
    if (target === 'en' && !onEn) {
      setLocale('en')
      navigate(toEnPath(pathname))
    } else if (target === 'zh' && onEn) {
      setLocale('zh')
      navigate(toZhPath(pathname))
    } else {
      setLocale(target)
    }
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => switchTo(activeLocale === 'zh' ? 'en' : 'zh')}
        aria-label={t('common.language', { defaultValue: 'Language' })}
        title={t('common.language', { defaultValue: 'Language' })}
        className={cn(
          'inline-flex h-9 items-center gap-1.5 rounded-full border border-enterprise-border bg-enterprise-surface px-2.5 font-mono text-xs text-enterprise-muted hover:text-enterprise-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg',
          className,
        )}
      >
        <Globe className="h-3.5 w-3.5" aria-hidden="true" />
        {activeLocale === 'zh' ? '中' : 'EN'}
      </button>
    )
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    // toggle between zh/en on arrow
    switchTo(activeLocale === 'zh' ? 'en' : 'zh')
  }

  return (
    <div
      role="radiogroup"
      aria-label={t('common.language', { defaultValue: 'Language' })}
      className={cn('inline-flex rounded-full border border-enterprise-border bg-enterprise-surface p-0.5', className)}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        role="radio"
        lang="zh"
        aria-checked={activeLocale === 'zh'}
        tabIndex={activeLocale === 'zh' ? 0 : -1}
        onClick={() => switchTo('zh')}
        className={cn(
          'inline-flex h-9 items-center rounded-full px-2 text-xs font-medium tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg sm:px-3',
          activeLocale === 'zh' ? 'bg-enterprise-bg text-enterprise-text shadow-sm' : 'text-enterprise-muted hover:text-enterprise-text',
        )}
      >
        中文
      </button>
      <button
        type="button"
        role="radio"
        lang="en"
        aria-checked={activeLocale === 'en'}
        tabIndex={activeLocale === 'en' ? 0 : -1}
        onClick={() => switchTo('en')}
        className={cn(
          'inline-flex h-9 items-center rounded-full px-2 text-xs font-medium tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg sm:px-3',
          activeLocale === 'en' ? 'bg-enterprise-bg text-enterprise-text shadow-sm' : 'text-enterprise-muted hover:text-enterprise-text',
        )}
      >
        EN
      </button>
    </div>
  )
}
